// 일정표/상세정보 통합 파서를 위한 유틸리티
// df_cellMerge 응답 HTML(origin.html 형태)를 분석하여
// 1) info-section 블록들을 타이틀/본문 쌍 배열로 변환
// 2) itinerary-section HTML과 CSS를 분리 추출

/**
 * df_cellMerge 응답 HTML을 파싱하여 일정소개/상세정보용 데이터로 변환
 * @param {string} htmlString - df_cellMerge에서 받은 전체 HTML 문자열
 * @returns {{ cssText: string, infoBlocks: Array<{ id: string, title: string, bodyHtml: string, rawHtml: string }>, itineraryHtml: string }}
 */
export function parseItineraryResponse(htmlString) {
  if (!htmlString) {
    return { cssText: '', headLinksHtml: '', infoBlocks: [], itineraryHtml: '' };
  }
  let originalInput = htmlString;
  let jsonEnvelope = null;
  // If server returned a JSON envelope (e.g., { status, html, subData, ... }), extract html first
  if (typeof htmlString === 'string') {
    const trimmed = htmlString.trim();
    if (trimmed.startsWith('{') && trimmed.includes('"html"')) {
      try {
        jsonEnvelope = JSON.parse(trimmed);
        if (jsonEnvelope && typeof jsonEnvelope.html === 'string') {
          htmlString = jsonEnvelope.html;
        }
      } catch {}
    }
  } else if (typeof htmlString === 'object' && htmlString !== null) {
    // Allow callers to pass the JSON directly
    jsonEnvelope = htmlString;
    if (typeof jsonEnvelope.html === 'string') {
      htmlString = jsonEnvelope.html;
    } else {
      htmlString = '';
    }
  }

  let cssText = '';
  let headLinksHtml = '';
  const infoBlocks = [];
  let itineraryHtml = '';
  const numberedKeyValues = {};

  try {
    if (jsonEnvelope && typeof jsonEnvelope === 'object') {
      const collect = (obj) => {
        if (!obj || typeof obj !== 'object') return;
        const keys = Object.keys(obj).filter((k) => /^\d+$/.test(String(k)));
        keys.sort((a, b) => Number(a) - Number(b));
        keys.forEach((k) => {
          const raw = String(obj[k] ?? '').trim();
          if (!raw) return;
          if (!Object.prototype.hasOwnProperty.call(numberedKeyValues, String(k))) {
            numberedKeyValues[String(k)] = raw;
          }
        });
      };
      collect(jsonEnvelope);
      if (jsonEnvelope.subData && typeof jsonEnvelope.subData === 'object') {
        collect(jsonEnvelope.subData);
      }
    }
  } catch (_) {
    // ignore
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(String(htmlString || ''), 'text/html');

    // 1) CSS 수집: 모든 <style> 태그의 내용을 합침
    const styleEls = Array.from(doc.querySelectorAll('style'));
    cssText = styleEls.map((el) => el.textContent || '').join('\n\n');

    // 1-a) Head 링크 수집: 아이콘 폰트/외부 CSS 등 (e.g., Google Fonts)
    try {
      const head = doc.querySelector('head') || doc;
      const links = Array.from(head.querySelectorAll('link[rel],link[href]'))
        .filter((lnk) => {
          const href = (lnk.getAttribute('href') || '').toLowerCase();
          const rel = (lnk.getAttribute('rel') || '').toLowerCase();
          if (!href) return false;
          // keep common stylesheet/font links
          return rel.includes('stylesheet') || href.includes('fonts.googleapis.com');
        })
        .map((lnk) => lnk.outerHTML || '');
      headLinksHtml = links.join('\n');
    } catch {}

    // 2) product-info / info-section 내의 블록들을 타이틀+본문 쌍으로 파싱
    const productInfo = doc.querySelector('div.product-info');
    const collectSections = (root) => Array.from(root.querySelectorAll('div.info-section'));
    const parseSections = (sections) => {
      sections.forEach((section, sectionIndex) => {
        // 이 섹션 내부에 단일 래퍼 DIV가 있고, 그 안에 타이틀/본문 그룹들이 반복되는 구조가 많음
        // 단일 래퍼를 계속 풀어서 실제 타이틀/본문 그룹들에 접근
        let children = Array.from(section.children || []);
        while (children.length === 1 && children[0] && children[0].tagName === 'DIV') {
          children = Array.from(children[0].children || []);
        }
        let currentTitleDiv = null;
        let currentBodyNodes = [];

        const flushBlock = () => {
          if (!currentTitleDiv) return;
          const title = (currentTitleDiv.textContent || '').trim();
          if (!title) {
            currentTitleDiv = null;
            currentBodyNodes = [];
            return;
          }
          const bodyHtml = currentBodyNodes.map((n) => n.outerHTML || '').join('');
          const bodyText = (() => {
            try {
              let h = bodyHtml || '';
              h = h.replace(/<br\s*\/?>(?=\s*\n?)/gi, '\n');
              // replace block-level tags with newlines to preserve structure minimally
              h = h.replace(/<\/(p|div|tr|li|h[1-6])>/gi, '\n');
              // strip all tags
              h = h.replace(/<[^>]+>/g, '');
              // decode common entities
              h = h.replace(/&nbsp;/g, ' ').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');
              // normalize whitespace
              return h.split('\n').map(s => s.trim()).join('\n').replace(/\n{3,}/g, '\n\n').trim();
            } catch { return ''; }
          })();
          const rawHtml = [currentTitleDiv, ...currentBodyNodes]
            .map((n) => n.outerHTML || '')
            .join('');

          const id = `info-${sectionIndex}-${infoBlocks.length}`;
          infoBlocks.push({ id, title, bodyHtml, bodyText, rawHtml });

          currentTitleDiv = null;
          currentBodyNodes = [];
        };

        const isTitleDiv = (el) => {
          if (!el || el.tagName !== 'DIV') return false;
          const text = (el.textContent || '').trim();
          if (!text) return false;
          // 테이블 래퍼(div 안에 table이 있는 경우)는 타이틀이 아닌 본문으로 간주
          if (el.querySelector('table')) return false;
          return true;
        };

        children.forEach((child) => {
          if (isTitleDiv(child)) {
            // 이전 블록이 있으면 먼저 마무리
            flushBlock();
            currentTitleDiv = child;
            currentBodyNodes = [];
          } else {
            if (currentTitleDiv) {
              currentBodyNodes.push(child);
            }
          }
        });

        // 마지막 블록 처리
        flushBlock();
      });
    };

    if (productInfo) {
      // info-section을 자식/후손 전체에서 수집하여 보다 관대하게 파싱
      const infoSections = collectSections(productInfo);
      parseSections(infoSections);
    }

    // product-info가 없거나 블록이 비어있으면 문서 전체에서 info-section을 탐색 (fallback)
    if (infoBlocks.length === 0) {
      const allSections = collectSections(doc);
      parseSections(allSections);
    }

    // 3) itinerary-section 전체 HTML 추출
    const itinerarySection = doc.querySelector('div.itinerary-section');
    if (itinerarySection) {
      // remove any nested <style> tags inside itinerary section to avoid double-inject when we add cssText separately
      const cloned = itinerarySection.cloneNode(true);
      Array.from(cloned.querySelectorAll('style')).forEach((el) => el.parentNode && el.parentNode.removeChild(el));
      itineraryHtml = cloned.outerHTML;
    }
  } catch (e) {
    console.error('parseItineraryResponse error:', e);
    // continue to "non-HTML" fallback below
  }

  // Fallback: some df_cellMerge responses include structured fields like "1":"타이틀||||본문" ...
  try {
    if (infoBlocks.length === 0 && jsonEnvelope && typeof jsonEnvelope === 'object') {
      const numberedKeys = Object.keys(jsonEnvelope)
        .filter((k) => /^\d+$/.test(k))
        .sort((a, b) => Number(a) - Number(b));
      for (const k of numberedKeys) {
        const raw = String(jsonEnvelope[k] ?? '').trim();
        if (!raw) continue;
        numberedKeyValues[String(k)] = raw;
        // Split by repeating pipes. Expect format: Title||||Body(HTML allowed)
        const parts = raw.split(/\|{2,}/g).map((s) => s.trim());
        if (parts.length >= 2) {
          const title = parts[0] || '';
          // body may be in the 2nd part; sometimes more parts exist but are empty or for layout
          // pick the longest non-empty as body
          const candidates = parts.slice(1).filter(Boolean);
          const body = candidates.sort((a, b) => b.length - a.length)[0] || '';
          if (title && body) {
            const bodyText = (() => {
              try {
                let h = body || '';
                h = h.replace(/<br\s*\/?>(?=\s*\n?)/gi, '\n');
                h = h.replace(/<\/(p|div|tr|li|h[1-6])>/gi, '\n');
                h = h.replace(/<[^>]+>/g, '');
                h = h.replace(/&nbsp;/g, ' ').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');
                return h.split('\n').map(s => s.trim()).join('\n').replace(/\n{3,}/g, '\n\n').trim();
              } catch { return ''; }
            })();
            infoBlocks.push({ id: `kv-${k}-${infoBlocks.length}`, title, bodyHtml: body, bodyText, rawHtml: `<div>${title}</div><div>${body}</div>`, rawValue: raw, kvKey: String(k) });
          }
        }
      }
    }
  } catch (e) {
    console.warn('Key-value fallback parse failed:', e);
  }

  const subData = (jsonEnvelope && typeof jsonEnvelope === 'object' && jsonEnvelope.subData) ? jsonEnvelope.subData : null;

  return { cssText, headLinksHtml, infoBlocks, itineraryHtml, subData, numberedKeyValues };
}

export default {
  parseItineraryResponse,
};
